import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus} from 'platform/components';
import {
  Box,
  getSize,
  Grid,
  Heading,
  HStack,
  Link,
  Scroll,
  Show,
  Stack,
  Text,
  useElementSize,
  VStack,
} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {useEffect} from 'react';

import {isNil, isNotEmpty, min} from 'ramda';
import {floor, isFalse, isNotNil} from 'ramda-adjunct';

import {useSourcingSetUserSeenMutation, useSourcingVehicleDetailQuery} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useSourcingCurrency} from '../../hooks/useSourcingCurrency';
import {useSourcingPricingStrategy} from '../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../hooks/useSourcingTargetCountry';
import {isUnifiedSourcingEnabled} from '../../utils/isUnifiedSourcingEnabled';
import {BpmEstimateCard} from '../BpmEstimate/components/BpmEstimateCard';
import {ClassifiedsSoldFlag} from '../ClassifiedsSoldFlag/ClassifiedsSoldFlag';
import {ListingPreviewSkeleton} from '../ListingPreview/components/ListingPreviewSkeleton';
import {VehicleFeature} from '../ListingPreview/components/VehicleFeature';
import {SourcingCebiaReportBadge} from '../SourcingCebiaReport/components/SourcingCebiaReportBadge';
import {AdServerLogo} from './components/AdServerLogo';
import {Gallery} from './components/Gallery';
import {PriceTable} from './components/PriceTable';
import {Seller} from './components/Seller';
import {VehicleDescription} from './components/VehicleDescription';
import {VehiclePreviewRedesign} from './components/VehiclePreviewRedesign';
import {VehicleSpecificationColumn} from './components/VehicleSpecificationColumn';
import {getAdLink} from './utils/getAdLink';

interface VehicleDetailProps extends TestIdProps {
  vehicleId: string | Nullish;
  isScrollDisabled?: boolean;
}

const CONTAINER_WIDTH_BREAKPOINT = 850;

export function VehiclePreview(props: VehicleDetailProps) {
  const formatNumber = useFormatNumber();
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();
  const pricingStrategy = useSourcingPricingStrategy();

  const [ref, width] = useElementSize<HTMLDivElement>();
  const isSmallContainerWidth = (width ?? 0) < CONTAINER_WIDTH_BREAKPOINT;
  const featureColumnsCount = isNil(width) ? 3 : min(floor(width / 200), 3);

  const {
    data: legacyData,
    currentData,
    isLoading,
    isFetching,
    isError,
  } = useSourcingVehicleDetailQuery(
    isNotNil(props.vehicleId)
      ? {
          externalId: props.vehicleId,
          currency: currency?.code,
          buyerCountry: targetCountry?.code,
          pricingStrategy,
        }
      : skipToken
  );
  const enhancedSurfaces = isUnifiedSourcingEnabled();
  const data = enhancedSurfaces ? currentData : legacyData;

  const sourcingVehicle = data?.sourcingVehicle;
  const originalPrice = data?.summary.original;

  const [setUserSeen] = useSourcingSetUserSeenMutation();
  useEffect(
    function markVehicleAsSeen() {
      if (isFalse(data?.seen) && isNotNil(data?.sourcingVehicle.adId)) {
        setUserSeen({
          body: {externalId: data.sourcingVehicle.adId},
        })
          .unwrap()
          .catch((error: ApiError) => handleApiError(error, {silent: true}));
      }
    },
    [data, setUserSeen]
  );

  // The estimate only means something to a buyer importing into the Netherlands.
  const isBpmEstimateVisible =
    isFeatureEnabled(featureFlags.SOURCING_NL_BPM) && targetCountry?.code === 'NLD';

  const isRedesignEnabled = isFeatureEnabled(featureFlags.SOURCING_VEHICLE_PREVIEW_REDESIGN);

  const adLink =
    isNotNil(sourcingVehicle?.url?.full) && isNotNil(targetCountry?.code)
      ? getAdLink(sourcingVehicle.url.full, targetCountry.code, i18n.language)
      : null;

  return (
    <Box
      backgroundColor={isRedesignEnabled ? 'palettes.neutral.20.100' : 'general.white'}
      height="100%"
      borderLeft="1px solid"
      borderColor="palettes.neutral.40.100"
      overflow="hidden"
      ref={ref}
      data-testid={props['data-testid']}
    >
      {enhancedSurfaces && isFetching && isNil(data) ? (
        <ListingPreviewSkeleton data-testid={props['data-testid']} />
      ) : (
        <DataStatus
          spacing={12}
          minHeight="100%"
          isLoading={!enhancedSurfaces && isLoading}
          isError={isError}
          isEmpty={isNil(props.vehicleId)}
        >
          <Scroll
            height={!props.isScrollDisabled ? `calc(100vh - ${getSize(12)})` : undefined}
            auto
          >
            <Box padding={4}>
              <Show when={isRedesignEnabled ? data : null}>
                {(detail) => (
                  <VehiclePreviewRedesign
                    detail={detail}
                    adLink={adLink}
                    isBpmEstimateVisible={isBpmEstimateVisible}
                    data-testid={props['data-testid']}
                  />
                )}
              </Show>
              <Show when={isRedesignEnabled ? null : sourcingVehicle}>
                {(sourcingVehicle) => (
                  <VStack spacing={6}>
                    <HStack justify="space-between" align="center">
                      {sourcingVehicle.server ? (
                        <AdServerLogo server={sourcingVehicle.server} maxHeight={8} maxWidth={32} />
                      ) : (
                        <Box />
                      )}
                      <Link
                        title={i18n.t('entity.sourcing.vehiclePreview.actions.openOriginalUrl')}
                        rightIcon="action/open_in_new"
                        size="small"
                        href={adLink}
                        target="_blank"
                        data-testid={suffixTestId('openOriginalUrl', props)}
                      />
                    </HStack>
                    <HStack spacing={2} align="center" wrap>
                      <Heading size={1}>{sourcingVehicle.adTitle}</Heading>
                      <SourcingCebiaReportBadge externalId={sourcingVehicle.adId} />
                      <ClassifiedsSoldFlag
                        sourcingVehicle={sourcingVehicle}
                        data-testid={suffixTestId('sold', props)}
                      />
                    </HStack>
                    <Stack
                      direction={isSmallContainerWidth ? 'column' : 'row'}
                      spacing={4}
                      width="100%"
                    >
                      <Gallery sourcingVehicle={sourcingVehicle} />
                      <VStack
                        spacing={10}
                        grow={1}
                        minWidth={isSmallContainerWidth ? undefined : 80}
                      >
                        <VStack grow={1} spacing={6}>
                          <Show
                            when={
                              isNotNil(originalPrice) && isNotNil(sourcingVehicle.price)
                                ? {originalPrice, price: sourcingVehicle.price}
                                : null
                            }
                          >
                            {(prices) => (
                              <PriceTable
                                originalPrice={prices.originalPrice}
                                price={prices.price}
                              />
                            )}
                          </Show>
                          <Show when={sourcingVehicle.price?.vatReclaimable}>
                            <Text size="xSmall" color="tertiary">
                              {`${i18n.t('entity.sourcing.vehiclePreview.labels.vatIncluded', {
                                vat: formatNumber(sourcingVehicle.price?.vatRate, 2),
                              })} (${sourcingVehicle.seller?.country?.translation})`}
                            </Text>
                          </Show>
                        </VStack>
                        <Show when={isBpmEstimateVisible}>
                          <BpmEstimateCard
                            sourcingVehicle={sourcingVehicle}
                            data-testid={suffixTestId('bpm', props)}
                          />
                        </Show>
                        <Show when={sourcingVehicle.seller}>
                          {(seller) => (
                            <Seller seller={seller} data-testid={suffixTestId('seller', props)} />
                          )}
                        </Show>
                      </VStack>
                    </Stack>
                    <VStack spacing={6}>
                      <Heading size={3}>
                        {i18n.t('entity.sourcing.vehiclePreview.labels.specification')}
                      </Heading>
                      <HStack spacing={4} wrap>
                        <VehicleSpecificationColumn
                          label={i18n.t('entity.vehicle.labels.year')}
                          icon="automotive/calendar"
                          value={sourcingVehicle.year}
                        />
                        <VehicleSpecificationColumn
                          label={i18n.t('entity.sourcing.vehiclePreview.labels.power')}
                          icon="automotive/engine"
                          value={
                            isNotNil(sourcingVehicle.power)
                              ? `${sourcingVehicle.power} ${i18n.t('general.metric.kW')}`
                              : null
                          }
                        />
                        <VehicleSpecificationColumn
                          label={i18n.t('entity.sourcing.vehiclePreview.labels.differential')}
                          icon="automotive/differential"
                          value={sourcingVehicle.driveType?.translation}
                        />
                        <VehicleSpecificationColumn
                          label={i18n.t('entity.sourcing.vehiclePreview.labels.mileage')}
                          icon="automotive/mileage"
                          value={
                            isNotNil(sourcingVehicle.mileage)
                              ? `${formatNumber(sourcingVehicle.mileage)} ${i18n.t(
                                  'general.metric.km'
                                )}`
                              : null
                          }
                        />
                        <VehicleSpecificationColumn
                          label={i18n.t('entity.sourcing.vehiclePreview.labels.transmission')}
                          icon="automotive/transmission"
                          value={sourcingVehicle.transmission?.translation}
                        />
                        <VehicleSpecificationColumn
                          label={i18n.t('entity.sourcing.vehiclePreview.labels.fuel')}
                          icon="automotive/fuel_type"
                          value={sourcingVehicle.fuelType?.translation}
                        />
                        <VehicleSpecificationColumn
                          label={i18n.t('entity.sourcing.vehiclePreview.labels.bodyType')}
                          icon="automotive/car"
                          value={sourcingVehicle.carStyle?.translation}
                        />
                      </HStack>
                    </VStack>
                    <Show when={isNotEmpty(sourcingVehicle?.features)}>
                      <VStack spacing={6}>
                        <Heading size={3}>
                          {i18n.t('entity.sourcing.vehiclePreview.labels.vehicleFeatures')}
                        </Heading>
                        <Grid columns={featureColumnsCount} spacing={2}>
                          {sourcingVehicle?.features
                            .filter((feature) => feature.key !== feature.translation)
                            .map((feature) => (
                              <VehicleFeature
                                key={feature.key}
                                name={feature.translation}
                                data-testid={suffixTestId(feature.key, props)}
                              />
                            ))}
                        </Grid>
                      </VStack>
                    </Show>
                    <Show when={sourcingVehicle.description}>
                      <VStack spacing={6}>
                        <Heading size={3}>
                          {i18n.t('entity.sourcing.vehiclePreview.labels.vehicleDescription')}
                        </Heading>
                        <VehicleDescription
                          description={sourcingVehicle.description}
                          data-testid={suffixTestId('vehicleDescription', props)}
                        />
                      </VStack>
                    </Show>
                  </VStack>
                )}
              </Show>
            </Box>
          </Scroll>
        </DataStatus>
      )}
    </Box>
  );
}
